// S2-CC20.6b.3: BudgetTreeView — composition that wraps useLLMBudgets()
// into a <Card> with a recursive <TreeNode> per root. Self-contained; no
// props — the hook owns all state.
//
// Renders one of four states:
//   1. loading (initial)        → inline "Loading budgets…" caption
//   2. error                    → inline banner + retry button
//   3. loaded, tree empty       → <EmptyState> ("No budgets configured")
//   4. loaded, tree populated   → one <TreeNode> per root entry
//
// Wireframe: docs/sprint-2/wireframes/s2-cc6-llm-control-center.html
// (Section 8 — Budget Tree). We mirror the card header structure but all
// class names are `airgent-llm-*` so we don't collide with the unprefixed
// legacy .card-title/.card-sub in LLMControl.css.
import { Card } from '../Card';
import { EmptyState } from '../EmptyState';
import { TreeNode } from '../TreeNode';
import { useLLMBudgets } from '../../hooks/useLLMBudgets';
import './BudgetTreeView.css';

// Pull out HH:MM:SS from an ISO timestamp for the card-sub "as of" caption.
// We format as local time (not UTC) because the dashboard is an operator-
// facing tool and the wireframe shows local wall-clock time (19:42:03).
function formatAsOf(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

export function BudgetTreeView() {
  const { tree, asOf, loading, error, refresh } = useLLMBudgets();

  // Only show the "pure loading" spinner on the very first fetch (no tree
  // yet). Subsequent refreshes don't blank out populated content.
  const initialLoading = loading && tree.length === 0 && !error;

  return (
    <Card variant="standard" padding="default" className="airgent-llm-budget-tree-view">
      <div className="airgent-llm-card-header">
        <div>
          <div className="airgent-llm-card-title">Budget Tree</div>
          <div className="airgent-llm-card-sub">
            platform → role → role_instance → agent_instance → job · spent_cents computed live
          </div>
        </div>
        <div className="airgent-llm-card-sub">
          as of {formatAsOf(asOf)} · cache 30s
        </div>
      </div>

      {/* Error banner — sits ABOVE content so operators see it even with
          stale tree data from a previous successful fetch. */}
      {error && (
        <div className="airgent-llm-error-banner" role="alert">
          <span>Failed to load budgets: {error}</span>
          <button
            type="button"
            className="airgent-llm-retry-btn"
            onClick={refresh}
          >
            Retry
          </button>
        </div>
      )}

      {/* 4-state body: loading / empty / populated. Error alone (with prior
          tree) keeps the tree visible; error + empty tree falls through to
          the loading-or-empty branches below. */}
      {initialLoading ? (
        <div className="airgent-llm-loading-caption">Loading budgets…</div>
      ) : tree.length === 0 && !error ? (
        <EmptyState
          title="No budgets configured"
          detail="Seed via S2-CC20.5 auto-seed"
          variant="solid"
        />
      ) : (
        <div className="airgent-llm-tree">
          {tree.map((node, i) => (
            // Key on scope_type+scope_id+period_start — a budget row is
            // uniquely identified by (scope, period) per the DB PK.
            <TreeNode
              key={`${node.scope_type}-${node.scope_id}-${node.period_start}-${i}`}
              node={node}
              depth={0}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
